import {
    buildUnitPriceDisplay,
    deriveVariantName,
    sortOffersByPrice,
} from '../services/ProductService.js';
import { escapeHtml, formatAmount } from '../../utils/validation.js';
import { formatTimeAgo } from '../../utils/formatters.js';
import currencyManager from '../../utils/currency.js';
import { buildMediaUrl } from '../utils/media.js';
import type { Offer, Price, Product, ProductVariant } from '../../types/index.js';

interface ProductApiResponse {
    success: boolean;
    data?: {
        product?: Product;
        variants?: DetailVariant[];
        currency?: any;
        [key: string]: any;
    };
    currency?: any;
}

interface DetailVariant extends ProductVariant {
    best_price?: string | number | null;
    best_base_price?: string | number | null;
    has_active_promotion?: boolean;
    images?: Array<{ url?: string; alt?: string; is_primary?: boolean }>;
    offers?: Offer[];
}

let currentProduct: Product | null = null;
let currentVariants: DetailVariant[] = [];
let selectedVariantId: number | null = null;

function money(value: string | number | null | undefined): string {
    return currencyManager.formatCurrencyDisplay(value);
}

function parsePrice(value: string | number | null | undefined): number {
    const parsed = parseFloat(String(value ?? ''));
    return Number.isFinite(parsed) ? parsed : Infinity;
}

function variantOffers(variant: DetailVariant | null | undefined): Offer[] {
    return sortOffersByPrice(Array.isArray(variant?.offers) ? variant.offers : []);
}

function bestOfferForVariant(variant: DetailVariant | null | undefined): Offer | null {
    const offers = variantOffers(variant);
    return offers.length ? offers[0] : null;
}

function bestVariant(): DetailVariant | null {
    let best: DetailVariant | null = null;
    let bestPrice = Infinity;
    currentVariants.forEach((variant) => {
        const offer = bestOfferForVariant(variant);
        const price = parsePrice(offer?.price?.effective_price || offer?.price?.price);
        if (price < bestPrice) {
            best = variant;
            bestPrice = price;
        }
    });
    return best;
}

function selectedVariant(): DetailVariant | null {
    if (!currentVariants.length) return null;
    return currentVariants.find((variant) => variant.id === selectedVariantId) || bestVariant() || currentVariants[0];
}

function sourceMeta(offer: Offer | null | undefined): { name: string; logo: string | null } {
    const name = offer?.store?.name || offer?.platform?.name || 'Unknown store';
    const logoPath = offer?.store?.logo_path || offer?.platform?.logo_path || null;
    return { name, logo: logoPath ? buildMediaUrl(logoPath) : null };
}

function amountLabel(variant: DetailVariant | null | undefined): string {
    if (!variant) return '';
    const count = Number(variant.count || 1);
    const amount = variant.amount;
    const total = variant.total_amount;
    const unit = variant.unit || '';
    if (count > 1 && amount && total) {
        return `${formatAmount(count)} x ${formatAmount(amount)}${unit} (${formatAmount(total)}${unit})`;
    }
    if (count > 1 && amount) {
        return `${formatAmount(count)} x ${formatAmount(amount)}${unit}`;
    }
    if ((total || amount) && unit) {
        return `${formatAmount(total || amount)}${unit}`;
    }
    return deriveVariantName(variant, '');
}

function variantTitle(variant: DetailVariant | null | undefined): string {
    if (!variant) return '';
    return deriveVariantName(variant, amountLabel(variant));
}

function priceBlock(offer: Offer | null, variant: DetailVariant | null, alignRight = false): string {
    if (!offer) return '<p class="text-sm text-slate-500">No price</p>';
    const price = offer.price || {} as Price;
    const effective = price.effective_price || price.price;
    const original = price.price;
    const isSale = Boolean(price.is_on_sale && original && effective && String(original) !== String(effective));
    const unit = buildUnitPriceDisplay(price, variant || undefined);
    const textAlign = alignRight ? 'text-right' : '';
    return `
        <div class="${textAlign}">
            ${isSale ? `<div class="price-original text-sm">${price.formatted_price || money(original)}</div>` : ''}
            <div class="price-current ${isSale ? '' : 'normal'}">${price.formatted_effective_price || money(effective)}</div>
            ${unit ? `<div class="text-sm text-slate-500 mt-1">${escapeHtml(unit)}</div>` : ''}
        </div>
    `;
}

function offerPrice(offer: Offer | null | undefined): number {
    return parsePrice(offer?.price?.effective_price || offer?.price?.price);
}

function offerSavingsBadge(offer: Offer | null | undefined): string {
    const price = offer?.price || {} as Price;
    const savings = price.savings_percentage ? Math.round(Number(price.savings_percentage)) : null;
    return savings ? `<span class="text-xs font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-md">-${savings}%</span>` : '';
}

function renderHeader(product: Product | null): void {
    const el = document.getElementById('product-header-content');
    if (!el || !product) return;
    const brand = product.brand?.name || '';
    const category = product.category ? ((product.category as any).full_path || product.category.name || '') : '';
    el.innerHTML = `
        <div>
            ${brand ? `<div class="text-xs font-semibold uppercase tracking-wide text-slate-500">${escapeHtml(brand)}</div>` : ''}
            <h1 class="text-2xl sm:text-3xl font-bold text-slate-950 leading-tight">${escapeHtml(product.display_name || product.name)}</h1>
            ${category ? `<div class="mt-1 inline-flex rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-sm font-medium text-slate-600">${escapeHtml(category)}</div>` : ''}
        </div>
    `;
}

function renderOverview(product: Product | null, variant: DetailVariant | null): void {
    const el = document.getElementById('product-overview-section');
    if (!el || !product || !variant) return;
    const offer = bestOfferForVariant(variant);
    const allOfferCount = currentVariants.reduce((sum, item) => sum + variantOffers(item).length, 0);
    const best = bestVariant();
    const isBest = best?.id === variant.id;
    const bestSource = sourceMeta(offer);
    const unit = offer ? buildUnitPriceDisplay(offer.price, variant) : '';
    const updated = offer?.last_updated_at ? formatTimeAgo(offer.last_updated_at) : '';
    const targetUrl = offer?.external_product_url || offer?.external_url || offer?.url || '';

    el.innerHTML = `
        <div class="p-4 border-b border-slate-200">
            <div class="flex items-start justify-between gap-4 flex-wrap">
                <div class="min-w-0">
                    <h2 class="text-xl font-bold text-slate-800">Best current option</h2>
                    <p class="text-sm text-slate-600 mt-1">${offer ? `${escapeHtml(amountLabel(variant) || variantTitle(variant))} from ${escapeHtml(bestSource.name)}` : 'No active store offer for this product yet.'}</p>
                </div>
                <div class="flex items-center gap-2">
                    ${isBest ? `<span class="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-md">Best package</span>` : ''}
                    ${offerSavingsBadge(offer)}
                </div>
            </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-4 p-4">
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div class="text-xs font-semibold uppercase tracking-wide text-slate-500">Best price</div>
                    <div class="mt-1 text-xl font-bold text-slate-950">${offer ? money(offer.price?.effective_price || offer.price?.price) : '-'}</div>
                </div>
                <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div class="text-xs font-semibold uppercase tracking-wide text-slate-500">Unit price</div>
                    <div class="mt-1 text-base font-bold text-slate-950">${unit ? escapeHtml(unit) : '-'}</div>
                </div>
                <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div class="text-xs font-semibold uppercase tracking-wide text-slate-500">Packages</div>
                    <div class="mt-1 text-xl font-bold text-slate-950">${currentVariants.length}</div>
                </div>
                <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div class="text-xs font-semibold uppercase tracking-wide text-slate-500">Offers</div>
                    <div class="mt-1 text-xl font-bold text-slate-950">${allOfferCount}</div>
                </div>
            </div>
            <div class="flex flex-col items-start md:items-end justify-center gap-2">
                ${updated ? `<div class="text-sm text-slate-500">Updated ${escapeHtml(updated)}</div>` : ''}
                ${targetUrl ? `<a href="${escapeHtml(targetUrl)}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center justify-center rounded-md bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">Open store</a>` : ''}
            </div>
        </div>
    `;
}

function renderVariantSelector(): void {
    const el = document.getElementById('variant-selector-section');
    if (!el) return;
    const tabs = currentVariants.map((variant) => {
        const offer = bestOfferForVariant(variant);
        const active = variant.id === selectedVariantId;
        return `
            <button type="button" class="variant-tab ${active ? 'active' : ''}" data-variant-id="${variant.id}">
                <div class="flex items-center justify-between gap-2">
                    <div class="text-sm font-bold text-slate-950 leading-tight">${escapeHtml(amountLabel(variant) || variantTitle(variant))}</div>
                    ${offerSavingsBadge(offer)}
                </div>
                <div class="mt-1 flex items-center justify-between gap-2 text-xs">
                    <span class="font-semibold text-slate-700">${offer ? escapeHtml(money(offer.price?.effective_price || offer.price?.price)) : 'No active offer'}</span>
                    ${offer ? `<span class="text-slate-500">${escapeHtml(buildUnitPriceDisplay(offer.price, variant) || '')}</span>` : ''}
                </div>
            </button>
        `;
    }).join('');

    el.innerHTML = `
        <div class="flex items-center justify-between gap-3 mb-3">
            <div>
                <h2 class="text-xl font-bold text-slate-800">Packages</h2>
                <p class="text-sm text-slate-600 mt-1">Pick a size, then compare store prices below.</p>
            </div>
        </div>
        <div class="grid grid-cols-1 gap-2">${tabs}</div>
    `;

    el.querySelectorAll<HTMLButtonElement>('[data-variant-id]').forEach((button) => {
        button.addEventListener('click', () => {
            selectedVariantId = Number(button.dataset.variantId);
            renderAll();
        });
    });
}

function offerRow(offer: Offer, variant: DetailVariant, isBest: boolean): string {
    const source = sourceMeta(offer);
    const updated = offer.last_updated_at ? formatTimeAgo(offer.last_updated_at) : '';
    const price = offer.price || {} as Price;
    const targetUrl = offer.external_product_url || offer.external_url || offer.url || '';
    return `
        <div class="offer-row rounded-lg border ${isBest ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-white'} shadow-sm">
            <div class="flex items-center gap-3 min-w-0">
                <div class="store-logo-frame">
                    ${source.logo ? `<img src="${escapeHtml(source.logo)}" alt="${escapeHtml(source.name)}" loading="lazy">` : `<span class="text-xs font-bold text-slate-500">${escapeHtml(source.name.slice(0, 2).toUpperCase())}</span>`}
                </div>
                <div class="min-w-0">
                    <div class="flex items-center gap-2">
                        <div class="font-bold text-slate-950 truncate">${escapeHtml(source.name)}</div>
                        ${isBest ? `<span class="text-[11px] font-bold text-emerald-700 bg-white border border-emerald-200 px-1.5 py-0.5 rounded">Best</span>` : ''}
                    </div>
                    <div class="text-xs text-slate-500">${updated ? `updated ${escapeHtml(updated)}` : 'update time unavailable'}</div>
                </div>
            </div>
            <div>
                <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Unit</div>
                <div class="text-sm font-bold text-slate-900">${escapeHtml(buildUnitPriceDisplay(price, variant) || '-')}</div>
            </div>
            <div class="flex items-center gap-3 justify-start md:justify-end">
                ${offerSavingsBadge(offer)}
                ${priceBlock(offer, variant, true)}
            </div>
            <div class="flex justify-start md:justify-end">
                ${targetUrl ? `<a href="${escapeHtml(targetUrl)}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center justify-center px-3 py-2 rounded-md bg-slate-950 text-white text-sm font-semibold hover:bg-slate-800">Store</a>` : ''}
            </div>
        </div>
    `;
}

function renderSelectedVariant(): void {
    const el = document.getElementById('selected-variant-section');
    const variant = selectedVariant();
    if (!el || !variant) return;
    const offers = variantOffers(variant);
    const rows = offers.map((offer, index) => offerRow(offer, variant, index === 0)).join('');
    el.innerHTML = `
        <div class="p-4 border-b border-slate-200">
            <div class="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 class="text-xl font-bold text-slate-800">Store offers</h2>
                    <p class="text-sm text-slate-600 mt-1">${escapeHtml(amountLabel(variant) || variantTitle(variant))}</p>
                </div>
                ${bestOfferForVariant(variant) ? priceBlock(bestOfferForVariant(variant), variant, true) : ''}
            </div>
        </div>
        <div class="p-3 space-y-2">${rows || '<div class="p-4 text-sm text-slate-500">No active offers for this package.</div>'}</div>
    `;
}

function renderComparison(): void {
    const el = document.getElementById('variant-comparison-section');
    if (!el) return;
    if (currentVariants.length <= 1) {
        el.innerHTML = '';
        return;
    }
    const rows = currentVariants.map((variant) => {
        const offer = bestOfferForVariant(variant);
        const active = variant.id === selectedVariantId;
        return `
            <button type="button" class="compare-row w-full text-left hover:bg-slate-50 ${active ? 'bg-slate-50' : 'bg-white'}" data-compare-variant-id="${variant.id}">
                <div>
                    <div class="font-semibold text-slate-950">${escapeHtml(amountLabel(variant) || variantTitle(variant))}</div>
                    <div class="text-sm text-slate-500">${variantOffers(variant).length} offer${variantOffers(variant).length === 1 ? '' : 's'}</div>
                </div>
                <div class="text-sm text-slate-500">${offer ? buildUnitPriceDisplay(offer.price, variant) : '-'}</div>
                <div class="font-bold text-slate-950">${offer ? money(offer.price?.effective_price || offer.price?.price) : '-'}</div>
            </button>
        `;
    }).join('');
    el.innerHTML = `
        <div class="p-4 border-b border-slate-200">
            <h2 class="text-xl font-bold text-slate-800">Package comparison</h2>
            <p class="text-sm text-slate-600 mt-1">Compare package price against unit price before choosing.</p>
        </div>
        <div>${rows}</div>
    `;
    el.querySelectorAll<HTMLButtonElement>('[data-compare-variant-id]').forEach((button) => {
        button.addEventListener('click', () => {
            selectedVariantId = Number(button.dataset.compareVariantId);
            renderAll();
        });
    });
}

function renderHistory(): void {
    const el = document.getElementById('price-history-section');
    const variant = selectedVariant();
    const offer = bestOfferForVariant(variant);
    if (!el || !variant || !offer) return;
    const history = Array.isArray((offer as any).price_history) ? (offer as any).price_history.slice(0, 8) : [];
    if (!history.length) {
        el.innerHTML = `
            <div class="p-4">
                <h2 class="text-xl font-bold text-slate-800">Recent price history</h2>
                <p class="text-sm text-slate-600 mt-1">No history available yet for the selected offer.</p>
            </div>
        `;
        return;
    }
    const rows = history.map((item: any) => {
        const date = item.valid_from ? new Date(item.valid_from).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
        return `
            <div class="history-row">
                <div class="font-medium text-slate-800">${escapeHtml(date)}</div>
                <div class="text-sm text-slate-500">${item.unit_price ? `${escapeHtml(money(item.unit_price))}/${escapeHtml((variant.unit || 'unit').toLowerCase())}` : ''}</div>
                <div class="font-bold text-slate-950">${escapeHtml(money(item.price))}</div>
            </div>
        `;
    }).join('');
    el.innerHTML = `
        <div class="p-4 border-b border-slate-200">
            <h2 class="text-xl font-bold text-slate-800">Recent price history</h2>
            <p class="text-sm text-slate-600 mt-1">Latest changes for the best offer on this package.</p>
        </div>
        <div>${rows}</div>
    `;
}

function renderError(message: string): void {
    const el = document.getElementById('selected-variant-section');
    if (el) {
        el.innerHTML = `<div class="p-6 text-center text-red-600 font-semibold">${escapeHtml(message)}</div>`;
    }
}

function renderAll(): void {
    const variant = selectedVariant();
    if (!selectedVariantId && variant) selectedVariantId = variant.id;
    renderHeader(currentProduct);
    renderOverview(currentProduct, variant);
    renderVariantSelector();
    renderSelectedVariant();
    renderComparison();
    renderHistory();
}

async function fetchAndRenderProduct(): Promise<void> {
    const dataEl = document.getElementById('product-data');
    const apiUrl = dataEl?.dataset.apiUrl;
    if (!apiUrl) return;

    try {
        const response = await fetch(`${apiUrl}?currency=${currencyManager.getCode()}&price_history_limit=60`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json() as ProductApiResponse;
        if (!json.success || !json.data) throw new Error('Invalid product response');

        const currencyInfo = json.data.currency || json.currency;
        if (currencyInfo) currencyManager.applyCurrencyInfo(currencyInfo);

        currentProduct = json.data.product || json.data as Product;
        currentVariants = (json.data.variants || currentProduct.variants || []) as DetailVariant[];
        selectedVariantId = bestVariant()?.id || currentVariants[0]?.id || null;
        renderAll();
    } catch (error) {
        console.error('Failed to fetch product:', error);
        renderError('Error loading product data');
    }
}

async function initProductDetailPage(): Promise<void> {
    await fetchAndRenderProduct();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductDetailPage);
} else {
    initProductDetailPage();
}

export { initProductDetailPage };
